import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

const GOAL_KEY_MAP = {
  'Buying': 'buying',
  'Selling': 'selling',
  'Selling my practice': 'selling_my_practice',
  'Selling part of my practice': 'selling_part_of_my_practice',
  'Find a Successor': 'find_a_successor',
  'Become a Successor': 'become_a_successor',
  'Merging': 'merging',
  'Partnering': 'partnering',
  'Forming a Partnership': 'forming_a_partnership',
  'Finding a Junior Partner': 'finding_a_junior_partner',
  'Becoming a Junior Partner': 'becoming_a_junior_partner',
  'Business Continuity': 'business_continuity',
  'Mentoring': 'mentoring',
  'Being Mentored': 'being_mentored',
  'Looking for Successor': 'looking_for_successor',
  'Currently not interested in Buying / Selling': 'currentlynotinterestedin_buying/selling',
};

const TransitionGoalsCard = ({ user, onEdit }) => {
  const intl = useIntl();
  const goals = user.transition_goals || [];

  const translateGoal = (goal) => {
    const key = GOAL_KEY_MAP[goal];
    if (!key) return goal;
    return intl.formatMessage({ id: `transition_goals.${key}`, defaultMessage: goal });
  };
  const hasGoals = goals.length > 0;

  // Awaiting first-approval gate. When the user has no committed motivation yet
  // (nil / no_motivation) but already has a pending motivation request, they are
  // waiting on a manager to approve their first persona. Showing the "Set Your
  // Transition Goals" editor here lets them re-edit goals mid-pending, which
  // drives `motivation` to match `pending_motivation` and produces a no-op
  // "Buyer→Buyer" approval (and previously force-approved them). Hide the whole
  // section until the pending request resolves so that edit path is closed.
  const motivationUnset = !user.motivation || user.motivation === 'no_motivation';
  const awaitingFirstApproval = motivationUnset && !!user.pending_motivation;
  if (awaitingFirstApproval) return null;

  // Server-authoritative gate (parity with the legacy `disableSelection` logic).
  // Editing is locked only when the company has not enabled transition-goal
  // changes and the user already has a committed motivation. Treat an absent
  // value as editable so the control never over-locks.
  const canEdit = user.can_edit_motivation !== false;

  // Empty state when no goals
  if (!hasGoals) {
    return (
      <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
          <svg className="w-8 h-8 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-succession-text-primary mb-2">
          {intl.formatMessage({ id: 'succession.profile_edit.sections.transition_goals.set_your_goals_title' })}
        </h3>
        <p className="text-sm text-succession-text-muted mb-6 max-w-md mx-auto">
          {intl.formatMessage({ id: 'succession.profile_edit.sections.transition_goals.set_your_goals_body' })}
        </p>
        <button
          onClick={onEdit}
          disabled={!canEdit}
          title={!canEdit ? intl.formatMessage({ id: 'succession.profile_edit.sections.transition_goals.locked_notice', defaultMessage: 'Your organization manages transition goal changes. Contact your administrator to update your goals.' }) : undefined}
          className={`inline-flex items-center gap-2 px-6 py-2.5 bg-succession-primary text-white rounded-lg transition-opacity ${canEdit ? 'hover:opacity-90' : 'opacity-50 cursor-not-allowed'}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6-4a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {intl.formatMessage({ id: 'succession.profile_edit.sections.transition_goals.add_transition_goals' })}
        </button>
        {!canEdit && (
          <p className="text-xs text-succession-text-muted mt-3">
            {intl.formatMessage({ id: 'succession.profile_edit.sections.transition_goals.locked_notice', defaultMessage: 'Your organization manages transition goal changes. Contact your administrator to update your goals.' })}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="bg-succession-bg-secondary rounded-lg border border-succession-border p-6">
      {/* Header with icon and edit button */}
      <div className="flex items-start gap-4 mb-6">
        {/* Icon */}
        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 flex-shrink-0">
          <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        </div>

        {/* Text */}
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-succession-text-primary mb-1">
            {intl.formatMessage({ id: 'succession.profile_edit.sections.transition_goals.interested_in' })}
          </h2>
          <p className="text-sm text-succession-text-muted">
            {intl.formatMessage({ id: 'succession.profile_edit.sections.transition_goals.current_goals' })}
          </p>
        </div>

        {/* Edit button — hidden when the company locks transition-goal changes */}
        {canEdit && (
          <button
            onClick={onEdit}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-succession-text-primary hover:bg-succession-bg-primary rounded-lg transition-colors flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {intl.formatMessage({ id: 'succession.profile_edit.common.edit' })}
          </button>
        )}
      </div>

      {/* Goals badges */}
      <div className="flex flex-wrap gap-3 mb-6">
        {goals.map((goal, index) => (
          <span
            key={index}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-gray-200 text-gray-800 text-sm font-medium"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" strokeWidth={2} />
              <circle cx="12" cy="12" r="3" fill="currentColor" />
            </svg>
            {translateGoal(goal)}
          </span>
        ))}
      </div>

      {/* Footer text */}
      <p className="text-sm text-succession-text-muted italic">
        {intl.formatMessage({ id: 'succession.profile_edit.sections.transition_goals.looking_to_connect' })}
      </p>

      {!canEdit && (
        <p className="text-xs text-succession-text-muted mt-3">
          {intl.formatMessage({ id: 'succession.profile_edit.sections.transition_goals.locked_notice', defaultMessage: 'Your organization manages transition goal changes. Contact your administrator to update your goals.' })}
        </p>
      )}
    </div>
  );
};

TransitionGoalsCard.propTypes = {
  user: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
};

export default TransitionGoalsCard;
